import { Branded } from 'io-ts';

import { Image } from '@catawiki/runtime-toolkit';

import {
  CollectionInfo,
  CollectionLandscapeImage,
  CollectionPortraitImage,
} from '@/shared/models/collection';

type ImageOrientation = 'portrait' | 'landscape';
type ImageUrl<Orientation extends ImageOrientation> = Branded<
  string,
  Orientation
>;
type PortraitImageUrl = ImageUrl<'portrait'>;
type LandscapeImageUrl = ImageUrl<'landscape'>;

const IMGPROXY_HOST = 'https://assets.catawiki.com';

export const ImageTransformationPreset = Image.ImageTransformationPreset;
export type ImageTransformationPreset = Image.ImageTransformationPreset;

export const wrapWithImgProxy = (
  imageUrl: string,
  preset: Image.ImageTransformationPreset,
  dpr: number
): string =>
  Image.transformImageUrl(IMGPROXY_HOST, imageUrl, {
    presets: [preset],
    dpr,
  });

/**
 *
 * @param img of a Portrait or Landscape image
 * @returns the input's url wrapped with image proxy
 *
 * We rely on branded types to keep the kind of the returned value (string)
 * branded as the input image's orientation
 */
const getUrl = <Img extends CollectionPortraitImage | CollectionLandscapeImage>(
  img: Img
): ImageUrl<(typeof img)['orientation']> => {
  const orientationToPreset: Record<
    ImageOrientation,
    ImageTransformationPreset
  > = {
    portrait: ImageTransformationPreset.COLLECTION_CARD_PORTRAIT,
    landscape: ImageTransformationPreset.COLLECTION_CARD_LANDSCAPE,
  };

  return wrapWithImgProxy(
    img.url,
    orientationToPreset[img.orientation],
    2
  ) as ImageUrl<(typeof img)['orientation']>;
};

export function getCollectionCardImagesUrls(images: CollectionInfo['images']): {
  portrait: [PortraitImageUrl, PortraitImageUrl, PortraitImageUrl];
  landscape: [LandscapeImageUrl, LandscapeImageUrl];
} {
  const [image1, image2, image3, image4, image5] = images;

  const portraitUrls: PortraitImageUrl[] = [image1, image2, image3].map(getUrl);
  const [p1, p2, p3] = portraitUrls;

  const landscapeUrls: LandscapeImageUrl[] = [image4, image5].map(getUrl);
  const [l1, l2] = landscapeUrls;

  // Returning a tuple instead of the result of the arrays so the returned type
  // is known by TS:
  // e.g [string, string] instead of string[]
  //
  // It's important to keep the following order in which p3 comes before p2,
  // and l2 before l1 so we maintain the same image order across platforms.
  // This is due how the css grid works in the CollectionCard
  return {
    portrait: [
      p1, // top left
      p3, // bottom middle
      p2, // top right
    ],
    landscape: [
      l2, // top middle
      l1, // bottom left
    ],
  };
}
